import type { MenuItemData } from '../api/public.api';

/** Built-in header links — CMS items are merged on top (add/override), never discarded. */
export const DEFAULT_HEADER_MENU: MenuItemData[] = [
  { id: -1, label: 'Home', url: '/', icon: 'Home', target: '_self' },
  { id: -2, label: 'Features', url: '/features', icon: 'Zap', target: '_self' },
  { id: -3, label: 'Blog', url: '/blog', icon: 'BookOpen', target: '_self' },
  { id: -4, label: 'Apps', url: '/apps', icon: 'Smartphone', target: '_self' },
  { id: -5, label: 'Pricing', url: '/pricing', icon: 'CreditCard', target: '_self' },
  { id: -6, label: 'Guide', url: '/guide', icon: 'BookOpen', target: '_self' },
  { id: -7, label: 'Docs', url: '/documentation', icon: 'FileText', target: '_self' },
  { id: -8, label: 'Contact', url: '/contact', icon: 'Mail', target: '_self' },
];

export function normalizeMenuUrl(url: string | null | undefined): string {
  if (!url) return '';
  const t = String(url).trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('mailto:') || t.startsWith('tel:')) {
    return t;
  }
  return t.startsWith('/') ? t : `/${t}`;
}

/** Key for merge: external keep as-is lowercase host+path; internal path without trailing slash (except `/`). */
export function menuUrlKey(url: string): string {
  const n = normalizeMenuUrl(url);
  if (!n) return '';
  if (/^https?:\/\//i.test(n) || n.startsWith('//') || n.startsWith('mailto:') || n.startsWith('tel:')) {
    return n.toLowerCase();
  }
  if (n === '/') return '/';
  return n.replace(/\/+$/, '').toLowerCase();
}

/**
 * Merge built-in header links with CMS header items.
 * - Defaults always keep unless a CMS item targets the same URL (then CMS wins label/icon).
 * - CMS items with a real URL are always added.
 * - Items without URL are skipped (would wrongly go to `/` and break navigation).
 */
export function mergeHeaderMenu(cmsItems: MenuItemData[] | null | undefined): MenuItemData[] {
  const ordered: MenuItemData[] = [];
  const keys = new Set<string>();

  const push = (item: MenuItemData) => {
    const url = normalizeMenuUrl(item.url);
    if (!url) return;
    const key = menuUrlKey(url);
    if (!key || keys.has(key)) return;
    keys.add(key);
    ordered.push({ ...item, url, target: item.target || '_self' });
  };

  const cms = Array.isArray(cmsItems) ? cmsItems : [];
  const cmsByKey = new Map<string, MenuItemData>();
  for (const item of cms) {
    const url = normalizeMenuUrl(item.url);
    if (!url) continue;
    cmsByKey.set(menuUrlKey(url), { ...item, url });
  }

  for (const def of DEFAULT_HEADER_MENU) {
    const key = menuUrlKey(def.url || '');
    const override = cmsByKey.get(key);
    if (override) {
      push({ ...def, ...override, url: override.url || def.url });
      cmsByKey.delete(key);
    } else {
      push(def);
    }
  }

  for (const item of cms) {
    const key = menuUrlKey(item.url || '');
    if (key && cmsByKey.has(key)) {
      push(cmsByKey.get(key)!);
    }
  }

  return ordered.length ? ordered : DEFAULT_HEADER_MENU.map((d) => ({ ...d, url: d.url || '/' }));
}

/**
 * Safe href for a menu item: never fall back to homepage when URL missing.
 * Returns null when the item should not be a clickable link (e.g. footer column title).
 */
export function resolveMenuHref(url: string | null | undefined): string | null {
  const n = normalizeMenuUrl(url);
  return n || null;
}
